<template lang="pug">
.flexible-countdown(:class="customClass")
  template(v-if="displayMode === 'blocks'")
    template(v-for="(digit, index) in timeDigits")
      .time-block(
        v-if="digit.type !== 'day-text'"
        :key="`block-${index}`"
        :class="blockClass"
        :style="blockStyle"
      ) {{ digit.value }}
      .day-text(
        v-else
        :key="`day-text-${index}`"
        :class="dayTextClass"
        :style="dayTextStyle"
      ) {{ digit.value }}
      .separator(
        v-if="digit.showSeparator"
        :key="`sep-${index}`"
        :class="separatorClass"
        :style="separatorStyle"
      ) {{ separatorText }}

  template(v-else-if="displayMode === 'text'")
    span.countdown-text(:class="textClass" :style="textStyle") {{ formattedTime }}

  template(v-else-if="displayMode === 'slot'")
    slot(
      :timeData="timeData"
      :formattedTime="formattedTime"
      :isExpired="isExpired"
    )
</template>

<script>
import dayjs from 'dayjs';

export default {
  name: 'FlexibleCountdown',
  props: {
    type: {
      type: String,
      default: 'daily',
      validator: (value) => ['daily', 'target', 'duration'].includes(value),
    },
    targetTime: {
      type: [String, Number, Date],
      default: null,
    },
    duration: {
      type: Number,
      default: 0,
    },
    displayMode: {
      type: String,
      default: 'blocks',
      validator: (value) => ['blocks', 'text', 'slot'].includes(value),
    },
    format: {
      type: String,
      default: 'HH:mm:ss',
    },
    customClass: {
      type: String,
      default: '',
    },
    blockClass: {
      type: String,
      default: '',
    },
    blockStyle: {
      type: Object,
      default: () => ({}),
    },
    separatorClass: {
      type: String,
      default: '',
    },
    separatorStyle: {
      type: Object,
      default: () => ({}),
    },
    separatorText: {
      type: String,
      default: ':',
    },
    textClass: {
      type: String,
      default: '',
    },
    textStyle: {
      type: Object,
      default: () => ({}),
    },
    dayTextClass: {
      type: String,
      default: '',
    },
    dayTextStyle: {
      type: Object,
      default: () => ({}),
    },
    autoStart: {
      type: Boolean,
      default: true,
    },
    expiredText: {
      type: String,
      default: '已结束',
    },
  },

  data() {
    return {
      timer: null,
      timeData: {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      },
      isExpired: false,
      startTime: null,
    };
  },

  computed: {
    formattedTime() {
      if (this.isExpired) {
        return this.expiredText;
      }

      const {
        days, hours, minutes, seconds,
      } = this.timeData;
      const pad = (n) => String(n).padStart(2, '0');

      switch (this.format) {
        case 'DD天HH:mm:ss':
          return days > 0
            ? `${days}天${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
            : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
        case 'HH:mm:ss':
          return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
        case 'HH:mm':
          return `${pad(hours)}:${pad(minutes)}`;
        case 'mm:ss':
          return `${pad(minutes)}:${pad(seconds)}`;
        default:
          return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
      }
    },

    timeDigits() {
      if (this.isExpired) {
        return [];
      }

      const {
        days, hours, minutes, seconds,
      } = this.timeData;
      const pad = (n) => String(n).padStart(2, '0');
      const digits = [];

      if (this.format.includes('DD') && days > 0) {
        String(days).split('').forEach((digit) => {
          digits.push({
            value: digit,
            type: 'day',
            showSeparator: false, // 天数部分不显示冒号分隔符
          });
        });
        // 在天数后面添加"天"文字
        digits.push({
          value: '天',
          type: 'day-text',
          showSeparator: false,
        });
      }

      if (this.format.includes('HH')) {
        pad(hours).split('').forEach((digit, index, arr) => {
          digits.push({
            value: digit,
            type: 'hour',
            showSeparator: index === arr.length - 1 && this.format.includes('mm'),
          });
        });
      }

      if (this.format.includes('mm')) {
        pad(minutes).split('').forEach((digit, index, arr) => {
          digits.push({
            value: digit,
            type: 'minute',
            showSeparator: index === arr.length - 1 && this.format.includes('ss'),
          });
        });
      }

      if (this.format.includes('ss')) {
        pad(seconds).split('').forEach((digit) => {
          digits.push({
            value: digit,
            type: 'second',
            showSeparator: false,
          });
        });
      }

      return digits;
    },
  },

  watch: {
    targetTime() {
      this.restart();
    },
    duration() {
      this.restart();
    },
    type() {
      this.restart();
    },
  },

  mounted() {
    if (this.autoStart) {
      this.start();
    }
  },

  beforeDestroy() {
    this.stop();
  },

  methods: {
    start() {
      this.stop();
      this.startTime = Date.now();
      this.updateTime();
      this.timer = setInterval(this.updateTime, 1000);
      this.$emit('start');
    },

    stop() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
      this.$emit('stop');
    },

    restart() {
      this.isExpired = false;
      if (this.autoStart) {
        this.start();
      }
    },

    pause() {
      this.stop();
      this.$emit('pause');
    },

    resume() {
      if (!this.timer && !this.isExpired) {
        this.timer = setInterval(this.updateTime, 1000);
        this.$emit('resume');
      }
    },

    updateTime() {
      let targetTimestamp;

      switch (this.type) {
        case 'daily':
          targetTimestamp = dayjs().endOf('day').valueOf();
          break;
        case 'target':
          if (!this.targetTime) {
            this.handleExpired();
            return;
          }
          targetTimestamp = dayjs(this.targetTime).valueOf();
          break;
        case 'duration':
          if (!this.startTime || this.duration <= 0) {
            this.handleExpired();
            return;
          }
          targetTimestamp = this.startTime + (this.duration * 1000);
          break;
        default:
          this.handleExpired();
          return;
      }

      const now = Date.now();
      const diff = targetTimestamp - now;

      if (diff <= 0) {
        this.handleExpired();
        return;
      }

      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / (24 * 3600));
      const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.timeData = {
        days, hours, minutes, seconds,
      };

      this.$emit('tick', {
        ...this.timeData,
        totalSeconds,
        formattedTime: this.formattedTime,
      });
    },

    handleExpired() {
      this.isExpired = true;
      this.timeData = {
        days: 0, hours: 0, minutes: 0, seconds: 0,
      };
      this.stop();
      this.$emit('expired');
    },
  },
};
</script>

<style lang="less" scoped>
.flexible-countdown {
  display: inline-flex;
  align-items: center;

  .time-block {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    color: #000;
    border-radius: 0.04rem;
    font-size: 0.14rem;
    font-weight: 500;

    &:first-child {
      margin-left: 0;
    }

    &:last-child {
      margin-right: 0;
    }
  }

  .separator {
    color: #000;
    font-size: 0.14rem;
    font-weight: 500;
  }

  .day-text {
    color: #000;
    font-size: 0.14rem;
    font-weight: 500;
    margin: 0 0.02rem;
  }

  .countdown-text {
    font-size: 0.14rem;
    color: #333;
    font-weight: 500;
  }
}
</style>
